import * as React from "react";

import type { ProjectLogOut, ProjectStatus } from "@/entities/projects/types";
import { useAuthStore } from "@/shared/lib/auth-store";

interface StreamState {
  status: ProjectStatus | null;
  progress: number;
  currentStep: string | null;
  logs: ProjectLogOut[];
  done: boolean;
  /** Открыт ли коннект. Меняется во время реконнектов. */
  connected: boolean;
}

const TERMINAL: ReadonlySet<ProjectStatus> = new Set([
  "done",
  "partial",
  "failed",
  "cancelled",
]);

/**
 * SSE-подписка на `/api/projects/:id/stream` через fetch + ReadableStream.
 * Используем именно fetch (не EventSource), потому что EventSource не
 * умеет проставлять заголовок `Authorization`.
 *
 * При обрыве коннекта (но не на терминальном статусе) пытаемся
 * реконнектнуться через 3 секунды.
 *
 * `wantStream` — внешний сигнал, что проект сейчас должен быть активным
 * (статус queued/collecting/clustering). Нужен для сценария «Расширить»:
 * первый раз страница открылась на готовом проекте → SSE прислал `done`
 * → `state.done=true` → коннект закрылся. После expand'а статус снова
 * становится queued, но без этого флага мы бы не реконнектились
 * (висим в done-режиме). Когда `wantStream` переходит из false в true
 * при `state.done=true` — сбрасываем стейт и реконнектимся заново.
 */
const INITIAL_STATE: StreamState = {
  status: null,
  progress: 0,
  currentStep: null,
  logs: [],
  done: false,
  connected: false,
};

export function useProjectStream(
  projectId: string,
  wantStream: boolean = true,
): StreamState {
  const [state, setState] = React.useState<StreamState>(INITIAL_STATE);
  const [restartKey, setRestartKey] = React.useState(0);
  const prevWantRef = React.useRef(wantStream);

  // Внешний переход «не нужен → нужен» при «уже done» — сбрасываем стейт
  // и инкрементируем ключ, чтобы effect ниже переоткрыл коннект.
  React.useEffect(() => {
    const wasWant = prevWantRef.current;
    prevWantRef.current = wantStream;
    if (!wasWant && wantStream && state.done) {
      setState(INITIAL_STATE);
      setRestartKey((k) => k + 1);
    }
  }, [wantStream, state.done]);

  React.useEffect(() => {
    if (!projectId) return;
    let aborted = false;
    let controller: AbortController | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const token = useAuthStore.getState().token;

    const apply = (event: string, data: Record<string, unknown>): void => {
      setState((prev) => {
        if (prev.done) return prev;
        const next: StreamState = { ...prev };
        if (event === "status") {
          next.status = (data.status as ProjectStatus) ?? prev.status;
          if (typeof data.progress === "number") next.progress = data.progress;
          if (typeof data.current_step === "string" || data.current_step === null) {
            next.currentStep = (data.current_step as string | null) ?? null;
          }
        } else if (event === "progress") {
          if (typeof data.progress === "number") next.progress = data.progress;
          if (typeof data.current_step === "string" || data.current_step === null) {
            next.currentStep = (data.current_step as string | null) ?? null;
          }
        } else if (event === "log") {
          const row: ProjectLogOut = {
            level: String(data.level ?? "info"),
            message: String(data.message ?? ""),
            ts: String(data.ts ?? new Date().toISOString()),
          };
          next.logs = [...prev.logs, row];
        } else if (event === "done") {
          next.done = true;
          next.status = (data.status as ProjectStatus) ?? next.status;
          next.connected = false;
        } else if (event === "error") {
          next.done = true;
          next.connected = false;
        }
        return next;
      });
    };

    const connect = async (): Promise<void> => {
      controller = new AbortController();
      try {
        const resp = await fetch(`/api/projects/${projectId}/stream`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!resp.ok || !resp.body) {
          throw new Error(`HTTP ${resp.status}`);
        }
        if (!aborted) {
          setState((p) => ({ ...p, connected: true }));
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (!aborted) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE-кадры разделены `\n\n`.
          let sepIdx;
          while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
            const frame = buffer.slice(0, sepIdx);
            buffer = buffer.slice(sepIdx + 2);

            let evt = "message";
            let dataStr = "";
            for (const line of frame.split("\n")) {
              if (line.startsWith("event:")) {
                evt = line.slice(6).trim();
              } else if (line.startsWith("data:")) {
                dataStr += line.slice(5).trim();
              }
            }
            if (!dataStr) continue;
            try {
              apply(evt, JSON.parse(dataStr) as Record<string, unknown>);
            } catch {
              // невалидный JSON — игнор
            }
          }
        }
      } catch (e) {
        if (aborted) return;
        // ignore abort
        if ((e as { name?: string }).name === "AbortError") return;
      } finally {
        if (!aborted) {
          setState((p) => ({ ...p, connected: false }));
        }
      }

      if (aborted) return;
      // Reconnect, если статус не терминальный.
      setState((p) => {
        if (p.done || (p.status && TERMINAL.has(p.status))) return p;
        reconnectTimer = setTimeout(() => {
          if (!aborted) void connect();
        }, 3000);
        return p;
      });
    };

    void connect();

    return () => {
      aborted = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      controller?.abort();
    };
  }, [projectId, restartKey]);

  return state;
}
